import { addDays, addMonths, addWeeks } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatDate, normalizeDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { Constants, CreditApplication } from '@src/data/types'
import {
  IFinanceBaseWorksheet,
  IFinanceBaseWorksheetDecisionDTOSchema,
  IFinanceBaseWorksheetRevision,
} from '@src/data/types/IFinanceBaseWorksheetSchema'

const buildMechanicalWarrantyAdditionsSchema = () =>
  yup.object({
    options: yup
      .array()
      .of(yup.string().defined())
      .required()
      .default([]),
    surcharges: yup
      .array()
      .of(yup.string().defined())
      .required()
      .default([]),
  })

export type Additions = yup.InferType<ReturnType<typeof buildMechanicalWarrantyAdditionsSchema>>

const validateMechWarrantyDeliveryOnRange = (
  val: string,
  ctx: yup.TestContext<yup.AnyObject>,
): boolean | yup.ValidationError => {
  const deliveryOn = (ctx.parent as MechanicalWarrantyWorksheet).deliveryOn
  const paymentFrequency = (ctx.parent as MechanicalWarrantyWorksheet).paymentFrequency
  if (!val || !deliveryOn || !paymentFrequency) return true

  const [minDate, maxDate] = getFirstPaymentOnMinMax(deliveryOn, paymentFrequency)
  const normalizedVal = normalizeDateTime(val)

  if (normalizedVal < minDate) {
    return ctx.createError({ message: t('validation:date.min', { min: formatDate(minDate) }) })
  }

  if (normalizedVal > maxDate) {
    return ctx.createError({ message: t('validation:date.max', { max: formatDate(maxDate) }) })
  }

  return true
}

export const getFirstPaymentOnMinMax = (
  deliveryOn: string | Date,
  paymentFrequency: EPaymentFrequency,
): [Date, Date] => {
  const deliveryDate = normalizeDateTime(deliveryOn)
  switch (paymentFrequency) {
    case EPaymentFrequency.Monthly:
      return [addDays(deliveryDate, 2), addMonths(deliveryDate, 1)]
    case EPaymentFrequency.BiWeekly:
      return [addDays(deliveryDate, 2), addWeeks(deliveryDate, 2)]
    case EPaymentFrequency.Single:
      return [addDays(deliveryDate, 2), addWeeks(deliveryDate, 1)]
    default:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 5)]
  }
}

export const buildMechanicalWarrantyWorksheetSchema = (creditApplication?: CreditApplication) => {
  const config = Constants.financingProgramConfigs[EFinancingProgram.MechanicalWarranty]
  const baseAdditionsSchema = buildMechanicalWarrantyAdditionsSchema()
  return yup.object({
    amountRequested: yup.double().required().positive(),
    deliveryOn: yup
      .string()
      .dateOnly()
      .default(() => formatDate(new Date()))
      .nullable()
      .required(),

    paymentFrequency: yup
      .mixed<EPaymentFrequency>()
      .default(creditApplication?.isSinglePayment ? EPaymentFrequency.Single : EPaymentFrequency.Monthly)
      .required()
      .test('valid-payment-frequency', function (value) {
        if (creditApplication?.isSinglePayment) {
          return (
            value === EPaymentFrequency.Single ||
            this.createError({
              message: t('common.errors.singlePaymentOnly'),
            })
          )
        } else {
          return (
            value === EPaymentFrequency.Monthly ||
            value === EPaymentFrequency.BiWeekly ||
            this.createError({
              message: t('common.errors.monthlyOrBiWeeklyOnly'),
            })
          )
        }
      }),
    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(EPaymentPlan.RegularDailyInterests),
    term: yup
      .integer()
      .min(config.supportedTerms[0])
      .max(config.supportedTerms[config.supportedTerms.length - 1])
      .nonNullable()
      .required(),

    creditApplicationId: yup.string().default(null).nullable(),

    id: yup.string().default(null).nullable(),

    versionTag: yup.string().default(''),

    isHighEstimate: yup.bool().default(false),

    firstPaymentOn: yup
      .string()
      .dateOnly()
      .required()
      .test('deliveryOn-range', (val, ctx) => validateMechWarrantyDeliveryOnRange(val, ctx))
      .default(() => formatDate(addDays<Date>(new Date(), 2))),
    additions: baseAdditionsSchema.default(baseAdditionsSchema.getDefault()).nullable(),
    status: yup.string().default(EWorksheetStatus.Draft).required(),
    providerId: yup.string().required(),
    policy: yup.string().required().default(null),
    mechanicalWarrantyProductId: yup.string().required().default(null),
  })
}

export type MechanicalWarrantyWorksheet = IFinanceBaseWorksheet & {
  additions: Additions
  providerId: string
  policy: string
  mechanicalWarrantyProductId: string
}

export type MechanicalWarrantyWorksheetRevision = IFinanceBaseWorksheetRevision

export type MechanicalWarrantyWorksheetDto = yup.InferType<ReturnType<typeof buildMechanicalWarrantyWorksheetSchema>>

export type MechanicalWarrantyDecisionDTO = yup.InferType<typeof IFinanceBaseWorksheetDecisionDTOSchema>